import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { BoardSummary } from "../../api/client";

function formatBoardUpdated(iso: string): string {
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}.${mm}.${yyyy} · ${hh}:${min}`;
  } catch {
    return "";
  }
}

function IconStar({ filled }: { filled: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.77 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"
        stroke="currentColor"
        strokeWidth="1.6"
        fill={filled ? "currentColor" : "none"}
        strokeLinejoin="round"
      />
    </svg>
  );
}

type MenuAction = {
  id: string;
  label: string;
  danger?: boolean;
  onClick: () => void;
};

/** Размеры всплывающего меню — по ним считается, хватает ли места снизу. */
const MENU_WIDTH = 200;
const MENU_ITEM_HEIGHT = 37;
const MENU_PADDING = 12;
const VIEWPORT_GAP = 8;

type Props = {
  board: BoardSummary;
  cabinetSection: "mine" | "shared";
  duplicateBusy: boolean;
  starBusy: boolean;
  onToggleStar: (boardId: string, next: boolean) => void;
  onRename: (board: BoardSummary) => void;
  onMove: (board: BoardSummary) => void;
  onDuplicate: (boardId: string) => void;
  onDelete: (boardId: string) => void;
  onShare: (board: BoardSummary) => void;
  /** Закрепить доску в левой панели (или открепить). */
  onTogglePin: (boardId: string, next: boolean) => void;
  /** Убрать у себя доску, добавленную по ссылке. */
  onRemoveShortcut: (board: BoardSummary) => void;
};

export function CabinetBoardRow({
  board,
  cabinetSection,
  duplicateBusy,
  starBusy,
  onToggleStar,
  onRename,
  onMove,
  onDuplicate,
  onDelete,
  onShare,
  onTogglePin,
  onRemoveShortcut,
}: Props) {
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const menuOpen = menuPos !== null;
  const menuRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const isShared =
    board.is_owner === false ||
    board.access_via === "folder" ||
    board.access_via === "board_member" ||
    board.access_via === "shortcut";
  const roleLabel =
    board.access_via === "shortcut"
      ? "По ссылке"
      : board.access_via === "folder"
        ? board.folder_access_role === "viewer"
          ? "Просмотр"
          : "Редактор"
        : board.membership_role === "viewer"
          ? "Просмотр"
          : "Редактор";
  const updated = formatBoardUpdated(board.updated_at);

  const menuItems: MenuAction[] = [];
  if (!isShared && cabinetSection === "mine") {
    menuItems.push(
      { id: "rename", label: "Название и описание", onClick: () => onRename(board) },
      { id: "move", label: "Перенести", onClick: () => onMove(board) },
      {
        id: "duplicate",
        label: duplicateBusy ? "Копируем…" : "Дублировать",
        onClick: () => onDuplicate(board.id),
      },
      { id: "share", label: "Поделиться", onClick: () => onShare(board) },
      { id: "delete", label: "Удалить", danger: true, onClick: () => onDelete(board.id) }
    );
  } else {
    menuItems.push({ id: "open", label: "Открыть", onClick: () => {} });
  }

  // Закрепление и «убрать у себя» доступны в обоих разделах.
  menuItems.push({
    id: "pin",
    label: board.is_pinned ? "Убрать из панели" : "Показывать в панели",
    onClick: () => onTogglePin(board.id, !board.is_pinned),
  });
  if (board.is_shortcut) {
    menuItems.push({
      id: "forget",
      label: "Убрать у себя",
      danger: true,
      onClick: () => onRemoveShortcut(board),
    });
  }

  const closeMenu = useCallback(() => setMenuPos(null), []);

  /**
   * Меню рисуется в портале с position: fixed, а не внутри карточки:
   * список досок прокручивается (overflow-y: auto) и обрезал бы выпадашку.
   */
  const toggleMenu = useCallback(() => {
    if (menuRef.current) {
      setMenuPos(null);
      return;
    }
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const height = menuItems.length * MENU_ITEM_HEIGHT + MENU_PADDING;
    const fitsBelow = rect.bottom + 4 + height <= window.innerHeight - VIEWPORT_GAP;
    const top = fitsBelow
      ? rect.bottom + 4
      : Math.max(VIEWPORT_GAP, rect.top - 4 - height);
    const left = Math.min(
      Math.max(VIEWPORT_GAP, rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - VIEWPORT_GAP
    );
    setMenuPos({ top, left });
  }, [menuItems.length]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocPointer(e: MouseEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (btnRef.current?.contains(target)) return;
      closeMenu();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeMenu();
    }
    // Прокрутка или смена размера уводят кнопку — позиция меню перестаёт быть верной.
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", closeMenu, true);
    window.addEventListener("resize", closeMenu);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", closeMenu, true);
      window.removeEventListener("resize", closeMenu);
    };
  }, [menuOpen, closeMenu]);

  const menu =
    menuOpen && menuPos
      ? createPortal(
          <div
            className="wb-board-card__menu wb-board-card__menu--floating"
            role="menu"
            ref={menuRef}
            style={{ top: menuPos.top, left: menuPos.left, width: MENU_WIDTH }}
          >
            {menuItems.map((item) =>
              item.id === "open" ? (
                <Link
                  key={item.id}
                  className="wb-board-card__menu-item"
                  to={`/board/${board.id}`}
                  role="menuitem"
                  onClick={closeMenu}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  className={`wb-board-card__menu-item${item.danger ? " is-danger" : ""}`}
                  disabled={item.id === "duplicate" && duplicateBusy}
                  onClick={() => {
                    closeMenu();
                    item.onClick();
                  }}
                >
                  {item.label}
                </button>
              )
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <article
      className={`wb-board-card wb-board-card--list wb-lk-card${menuOpen ? " is-menu-open" : ""}`}
    >
      <div className="wb-board-card__row">
        <button
          type="button"
          className={`wb-board-card__star${board.is_starred ? " is-active" : ""}`}
          title={board.is_starred ? "Убрать из избранного" : "В избранное"}
          aria-label={board.is_starred ? "Убрать из избранного" : "В избранное"}
          disabled={starBusy}
          onClick={() => onToggleStar(board.id, !board.is_starred)}
        >
          <IconStar filled={Boolean(board.is_starred)} />
        </button>
        <Link className="wb-board-card__open-zone" to={`/board/${board.id}`}>
          <span className="wb-board-card__title">{board.title}</span>
          {isShared ? <span className="wb-board-card__badge">Совместная</span> : null}
          {updated ? (
            <span className="wb-board-card__meta">· Изменена {updated}</span>
          ) : null}
          {board.description ? (
            <span className="wb-board-card__description" title={board.description}>
              {board.description}
            </span>
          ) : null}
        </Link>
        <div className="wb-board-card__actions" role="group" aria-label="Действия">
          {isShared || cabinetSection === "shared" ? (
            <>
              <Link className="wb-board-card__action-btn" to={`/board/${board.id}`}>
                Открыть
              </Link>
              <span className="wb-board-card__meta">{roleLabel}</span>
            </>
          ) : null}
          <div className="wb-board-card__menu-wrap">
            <button
              type="button"
              ref={btnRef}
              className="wb-board-card__menu-btn"
              aria-label="Ещё"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={toggleMenu}
            >
              ⋯
            </button>
          </div>
        </div>
      </div>
      {menu}
    </article>
  );
}
